import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { AuthenticationService } from './service/security/authentication.service';
import { RealtimeBalanceService } from './service/security/realtime-balance.service';
import { BusinessAccountService } from './service/business-account.service';
import {
  ClientOperationConfirmationUiRequest,
  ClientOperationConfirmationUiService
} from './service/security/client-operation-confirmation-ui.service';
import { UserSessionService } from './user-session.service';
import { environment } from '../environments/environment';
import { PwaInstallService } from './service/security/pwa-install.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  public loggedIn: boolean = false;
  public isAdmin: boolean = false;
  public isCaissier: boolean = false;
  public isPartner: boolean = false;
  public isPlatformPartner: boolean = false;
  public isGestionnaire: boolean = false;
  public updateAvailable: boolean = false;
  public mobileNavOpen: boolean = false;
  public showDevDocsLink: boolean = !environment.production;

  private userSessionSubscription?: Subscription;
  private clientOperationModalSubscription?: Subscription;
  private routerEventsSubscription?: Subscription;

  clientOperationRequest: ClientOperationConfirmationUiRequest | null = null;
  clientOperationDecisionLoading: boolean = false;
  clientOperationDecisionError: string = '';

  @HostListener('window:beforeinstallprompt', ['$event'])
  onbeforeinstallprompt(e: Event): void {
    e.preventDefault();
    this.pwaInstallService.capturePrompt(e);
  }

  @HostListener('window:appinstalled')
  onAppInstalled(): void {
    this.pwaInstallService.clearPrompt();
  }

  constructor(
    private router: Router,
    private swUpdate: SwUpdate,
    private userSessionService: UserSessionService,
    private translate: TranslateService,
    private auth: AuthenticationService,
    private realtimeBalanceService: RealtimeBalanceService,
    private businessAccountService: BusinessAccountService,
    private clientOperationConfirmationUiService: ClientOperationConfirmationUiService,
    private pwaInstallService: PwaInstallService
  ) {
    this.translate.setFallbackLang('fr');
    this.userSessionService.reload();

    const savedLang = localStorage.getItem('lang');
    if (!savedLang) {
      localStorage.setItem('lang', 'fr');
    }

    let langToUse = localStorage.getItem('lang') || 'fr';
    const user = this.userSessionService.getUser();
    if (user?.lang) {
      langToUse = user.lang;
      localStorage.setItem('lang', user.lang);
    }
    this.translate.use(langToUse);

    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.subscribe((evt) => {
        if (evt.type === 'VERSION_READY') {
          this.updateAvailable = true;
        }
      });
    }
  }

  ngOnInit(): void {
    this.clientOperationModalSubscription = this.clientOperationConfirmationUiService.activeRequest$.subscribe(request => {
      this.clientOperationRequest = request;
      this.clientOperationDecisionLoading = false;
      this.clientOperationDecisionError = '';
    });

    this.userSessionSubscription = this.userSessionService.changes().subscribe(user => {
      if (user?.token) {
        this.realtimeBalanceService.start();
      } else {
        this.realtimeBalanceService.stop();
      }
      this.refreshAuthState();
    });

    this.routerEventsSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        this.closeMobileNav();
        this.refreshAuthState();
      });

    this.refreshAuthState();
    if (this.loggedIn) {
      this.realtimeBalanceService.start();
    }
  }

  ngOnDestroy(): void {
    this.userSessionSubscription?.unsubscribe();
    this.clientOperationModalSubscription?.unsubscribe();
    this.routerEventsSubscription?.unsubscribe();
    this.realtimeBalanceService.stop();
  }

  public reload(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }
    this.swUpdate.activateUpdate().then(() => document.location.reload());
  }

  public installPWA(): void {
    void this.pwaInstallService.install();
  }

  public shouldInstall(): boolean {
    return this.pwaInstallService.shouldShowInstall();
  }

  public isRunningStandalone(): boolean {
    return this.pwaInstallService.isRunningStandalone();
  }

  public toggleMobileNav(): void {
    this.mobileNavOpen = !this.mobileNavOpen;
  }

  public closeMobileNav(): void {
    this.mobileNavOpen = false;
  }

  public getCurrentAreaLabel(): string {
    if (!this.loggedIn) {
      return 'Visiteur';
    }
    if (this.isAdmin) {
      return 'Administration';
    }
    if (this.isCaissier) {
      return 'Caisse';
    }
    if (this.isGestionnaire) {
      return 'Gestionnaire';
    }
    if (this.isPlatformPartner || this.isPartner) {
      return 'Partenaire';
    }
    return 'Porte-monnaie';
  }

  public isClientArea(): boolean {
    return this.loggedIn
      && !this.isAdmin
      && !this.isCaissier
      && !this.isGestionnaire
      && !this.isPartner
      && !this.isPlatformPartner;
  }

  public getPartnerDashboardRoute(): string {
    return this.isPlatformPartner ? '/utilisateur/partner/dashboard' : '/utilisateur/partenaire/dashboard';
  }

  decideClientOperation(approve: boolean): void {
    const request = this.clientOperationRequest;
    if (!request || this.clientOperationDecisionLoading) {
      return;
    }

    this.clientOperationDecisionLoading = true;
    this.clientOperationDecisionError = '';

    this.businessAccountService.decideClientOperationConfirmation(request.confirmationId, approve)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.clientOperationDecisionLoading = false;
          this.clientOperationConfirmationUiService.completeCurrent(request.confirmationId);
        },
        error: () => {
          this.clientOperationDecisionLoading = false;
          this.clientOperationDecisionError = "Impossible d'envoyer la décision. Réessayez.";
        }
      });
  }

  dismissClientOperationModal(): void {
    if (!this.clientOperationRequest) {
      return;
    }
    this.clientOperationConfirmationUiService.completeCurrent(this.clientOperationRequest.confirmationId);
  }

  getClientOperationTypeLabel(operationType?: string): string {
    if (operationType === 'PURCHASE') {
      return 'Achat chez marchand';
    }
    if (operationType === 'DEPOSIT') {
      return 'Dépôt sur compte';
    }
    if (operationType === 'WITHDRAWAL') {
      return 'Retrait sur compte';
    }
    return 'Opération';
  }

  getClientOperationAmountLabel(request: ClientOperationConfirmationUiRequest | null): string {
    if (!request) {
      return '0 FCFA';
    }
    return this.formatMoney(request.amount, request.currency);
  }

  isWithdrawalOperation(request: ClientOperationConfirmationUiRequest | null): boolean {
    return !!request && request.operationType === 'WITHDRAWAL';
  }

  getWithdrawalAmountLabel(request: ClientOperationConfirmationUiRequest | null): string {
    if (!request) {
      return this.formatMoney(0, 'FCFA');
    }
    return this.formatMoney(request.amount, request.currency);
  }

  getWithdrawalTaxLabel(request: ClientOperationConfirmationUiRequest | null): string {
    if (!request) {
      return this.formatMoney(0, 'FCFA');
    }
    return this.formatMoney(request.taxAmount ?? 0, request.currency);
  }

  getWithdrawalFeeLabel(request: ClientOperationConfirmationUiRequest | null): string {
    if (!request) {
      return this.formatMoney(0, 'FCFA');
    }
    return this.formatMoney(request.feeAmount ?? 0, request.currency);
  }

  getWithdrawalTotalDebitLabel(request: ClientOperationConfirmationUiRequest | null): string {
    if (!request) {
      return this.formatMoney(0, 'FCFA');
    }
    const total = Number.isFinite(Number(request.totalDebitAmount))
      ? Number(request.totalDebitAmount)
      : Number(request.amount || 0) + Number(request.taxAmount || 0) + Number(request.feeAmount || 0);
    return this.formatMoney(total, request.currency);
  }

  getClientOperationExpiryLabel(expiresAt?: string): string {
    if (!expiresAt) {
      return 'Sans expiration';
    }
    const date = new Date(expiresAt);
    if (Number.isNaN(date.getTime())) {
      return 'Expiration inconnue';
    }
    return date.toLocaleString('fr-FR');
  }

  isClientOperationExpired(request: ClientOperationConfirmationUiRequest | null): boolean {
    if (!request?.expiresAt) {
      return false;
    }
    const expires = new Date(request.expiresAt);
    if (Number.isNaN(expires.getTime())) {
      return false;
    }
    return Date.now() > expires.getTime();
  }

  logout(): void {
    this.realtimeBalanceService.stop();
    this.closeMobileNav();
    localStorage.clear();
    this.userSessionService.setUser(null);
    this.refreshAuthState();
    this.router.navigateByUrl('/spay');
  }

  private refreshAuthState(): void {
    const roles = new Set<string>();
    const userRoles = this.userSessionService.getUser()?.roles || [];

    for (const role of userRoles) {
      if (role) {
        roles.add(role.toUpperCase().replace(/^ROLE_/, '').trim());
      }
    }

    const legacyRole = (localStorage.getItem('inTheRoleOf') || '').toUpperCase().replace(/^ROLE_/, '').trim();
    if (legacyRole) {
      roles.add(legacyRole);
    }

    this.loggedIn = this.auth.isAuthenticated() || this.hasStoredToken();

    this.isAdmin = this.hasAnyRole(roles, ['ADMIN', 'SUPER_ADMIN', 'BANK', 'AUDITOR']);
    this.isCaissier = this.hasAnyRole(roles, ['CAISSIER']);
    this.isGestionnaire = this.hasAnyRole(roles, ['GESTIONNAIRE']);
    this.isPartner = this.hasAnyRole(roles, [
      'MICROFINANCE',
      'MICROFINANCE_SUPERVISOR',
      'MICROFINANCE_CAISSE',
      'MICROFINANCE_COLLECTE',
      'MICROFINANCE_CREDIT'
    ]);
    this.isPlatformPartner = this.hasAnyRole(roles, ['PLATFORM_ADMIN', 'SPAY_OPERATOR']);

    if (!this.loggedIn) {
      return;
    }

    if (this.isAdmin || this.isCaissier || this.isGestionnaire || this.isPartner || this.isPlatformPartner) {
      return;
    }

    if (localStorage.getItem('tokenConnectedCaissieronline')) {
      this.isCaissier = true;
      return;
    }

    if (localStorage.getItem('tokenConnectedUserGestionaire')) {
      this.isGestionnaire = true;
      return;
    }

    if (localStorage.getItem('tokenConnectedPartneronline') || localStorage.getItem('tokenConnectedPartenaireonline')) {
      this.isPartner = true;
      return;
    }

    if (localStorage.getItem('administratorauthorization')) {
      this.isAdmin = true;
    }
  }

  private hasStoredToken(): boolean {
    return [
      'tokenConnectedUserWSU1234567GHTVB',
      'tokenConnectedUserOnline',
      'tokenConnectedCaissieronline',
      'tokenConnectedUserGestionaire',
      'tokenConnectedPartneronline',
      'tokenConnectedPartenaireonline'
    ].some(key => {
      const token = localStorage.getItem(key);
      return !!token && token.trim().length > 0;
    });
  }

  private hasAnyRole(roles: Set<string>, expectedRoles: string[]): boolean {
    return expectedRoles.some(role => roles.has(role));
  }

  private formatMoney(amount: number | string | undefined, currency: string | undefined): string {
    const numericAmount = Number(amount ?? 0);
    const safeAmount = Number.isFinite(numericAmount) ? numericAmount : 0;
    const safeCurrency = (currency || 'FCFA').toUpperCase();
    return `${safeAmount.toLocaleString('fr-FR')} ${safeCurrency}`;
  }
}
